/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import type { NodeProps } from '../contextree/types';

/**
 * @description
 * The FieldUtilsclass contains utility APIs that handle the field related operations
 */
class FieldUtils {
  /**
   * This function updates the array notation path of the target property into a Pega-specific page reference format.
   * Example referenceList = ".Questionsets[0].Questions" return = ".Questionsets(1).Questions"
   * @param referenceList - The array notation path of the target property to be updated.
   * @returns The updated reference list as a string.
   * @public
   * @example <caption> formatPageReference API will return updated referenceList in the format expected by dxapi</caption>
   * PCore.getFieldUtils().formatPageReference('.Questionsets[0].Questions')
   * returns -- '.Questionsets(1).Questions';
   */
  static formatPageReference(referenceList: string) {
    const updatedReferenceListsArr = referenceList?.split('.').map((currentItem) => {
      if (currentItem.includes('[')) {
        const startIndex = currentItem.indexOf('[');
        const lastIndex = currentItem.indexOf(']');
        const digitInString = currentItem.substring(startIndex + 1, lastIndex);
        const index = Number(digitInString);
        const updatedIndex = index + 1;
        return `${currentItem.substring(0, startIndex)}(${updatedIndex})${currentItem.substring(lastIndex + 1)}`;
      }
      return currentItem;
    });
    return updatedReferenceListsArr.join('.');
  }

  /**
   * This function updates the array notation path of the target property into a constellation specific page reference format.
   * Example referenceList = ".Questionsets(1).Questions" return = ".Questionsets[0].Questions"
   * @param referenceList - The array notation path of the target property to be updated.
   * @returns The updated reference list as a string.
   * @public
   * @example <caption> formatPageReference API will return updated referenceList in the format expected by dxapi</caption>
   * PCore.getFieldUtils().formatPageReference('.Questionsets(1).Questions')
   * returns -- '.Questionsets[0].Questions';
   */
  static formatePageReferenceToArrayIndex(referenceList: string) {
    const updatedReferenceListsArr = referenceList?.split('.').map((currentItem) => {
      if (currentItem.includes('(')) {
        const startIndex = currentItem.indexOf('(');
        const lastIndex = currentItem.indexOf(')');
        const digitInString = currentItem.substring(startIndex + 1, lastIndex);
        const index = Number(digitInString);
        if (!Number.isNaN(index)) {
          const updatedIndex = index - 1;
          return `${currentItem.substring(0, startIndex)}[${updatedIndex}]${currentItem.substring(lastIndex + 1)}`;
        }
      }
      return currentItem;
    });
    return updatedReferenceListsArr.join('.');
  }

  /**
   * This function returns the fieldLabel from c11nEnv configProps.
   * @param fieldC11n Constellation env object.
   * @returns The label of the field as string.
   * @private
   * @example <caption> getFieldLabel API will return the fieldLabel from c11nEnv configProps</caption>
   * PCore.getFieldUtils().getFieldLabel(fieldC11nEnv)
   * returns -- 'label';
   */
  static getFieldLabel(contextTreeNodeProps: NodeProps) {
    let label = contextTreeNodeProps?.label;

    if (!label && contextTreeNodeProps?.inheritedProps) {
      const inheritedProps: { label?: string; showLabel?: string } = contextTreeNodeProps?.inheritedProps;
      if (inheritedProps.showLabel ?? true) {
        label = inheritedProps.label;
      }
    }

    return label;
  }
}

export default FieldUtils;
